// Combobox (DESIGN §3.6.6): cmdk in Radix Popover. Eingabefeld mit Vorschlagsliste (Avatar/Icon 24,
// Titel, Unterzeile); `multiple` zeigt gewählte Werte als Chips im Feld, Backspace entfernt den
// letzten. Asynchron über `loadOptions` (entprellt 150 ms, Skeleton 3 Zeilen, „No matches“).
import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { CaretDownIcon, CheckIcon, XIcon } from '@phosphor-icons/react';
import { Command } from 'cmdk';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { fieldFrame, fieldIcon, type FieldSize } from './_lib/fieldStyles';
import { useFieldProps } from './_lib/fieldContext';
import { cmdkItem, floatingSurface } from './_lib/menuStyles';
import { useAsyncOptions, type OptionsLoader } from './_lib/useAsyncOptions';
import './_lib/motion.css';
import { useLayerStyle } from './_lib/layer';
import { cn } from '../basis/kern';

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  /** Avatar oder Icon 24. */
  leading?: ReactNode;
  disabled?: boolean;
}

interface BaseProps {
  options?: ComboboxOption[];
  /** Asynchrone Quelle; ersetzt die eingebaute Filterung. */
  loadOptions?: OptionsLoader<ComboboxOption>;
  /** Bekannte Einträge der Startwerte (Labels der Chips, bevor etwas geladen ist). */
  selectedOptions?: ComboboxOption[];
  placeholder?: string;
  emptyText?: string;
  size?: FieldSize;
  disabled?: boolean;
  'aria-label'?: string;
  className?: string;
  onBlur?: () => void;
}

export interface ComboboxSingleProps extends BaseProps {
  multiple?: false;
  value: string | null;
  onValueChange: (value: string | null) => void;
}

export interface ComboboxMultipleProps extends BaseProps {
  multiple: true;
  value: string[];
  onValueChange: (value: string[]) => void;
}

export type ComboboxProps = ComboboxSingleProps | ComboboxMultipleProps;

function OptionSkeleton() {
  return (
    <div aria-hidden="true" className="space-y-1 p-1">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex h-12 items-center gap-3 px-3">
          <span className="size-6 animate-skeleton rounded-full bg-skeleton" />
          <span className="h-3 w-40 animate-skeleton rounded-full bg-skeleton" />
        </div>
      ))}
    </div>
  );
}

export function Combobox(props: ComboboxProps) {
  const {
    options,
    loadOptions,
    selectedOptions,
    placeholder = 'Search…',
    emptyText = 'No matches',
    size = 'md',
    disabled,
    className,
    onBlur,
  } = props;
  const field = useFieldProps({});
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [known, setKnown] = useState<Record<string, ComboboxOption>>({});
  const async = useAsyncOptions(loadOptions, query, open);
  const layerStyle = useLayerStyle();
  const visible = loadOptions ? async.results : (options ?? []);

  const lookup = useMemo(() => {
    const map: Record<string, ComboboxOption> = { ...known };
    for (const option of [...(selectedOptions ?? []), ...(options ?? []), ...async.results])
      map[option.value] = option;
    return map;
  }, [known, selectedOptions, options, async.results]);

  const selectedValues = props.multiple ? props.value : props.value ? [props.value] : [];
  const isSelected = (value: string) => selectedValues.includes(value);

  const changeQuery = (next: string) => {
    setQuery(next);
    async.markLoading();
    if (!open) setOpen(true);
  };

  const openList = () => {
    if (disabled || open) return;
    setOpen(true);
    async.markLoading();
  };

  const select = (option: ComboboxOption) => {
    setKnown((prev) => ({ ...prev, [option.value]: option }));
    if (props.multiple) {
      props.onValueChange(
        isSelected(option.value)
          ? props.value.filter((v) => v !== option.value)
          : [...props.value, option.value],
      );
      setQuery('');
    } else {
      props.onValueChange(option.value);
      setQuery('');
      setOpen(false);
    }
  };

  const remove = (value: string) => {
    if (props.multiple) props.onValueChange(props.value.filter((v) => v !== value));
    else props.onValueChange(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && query === '' && props.multiple && props.value.length) {
      remove(props.value[props.value.length - 1] as string);
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    } else if (event.key === 'ArrowDown' && !open) {
      openList();
    }
  };

  const singleLabel = !props.multiple && props.value ? (lookup[props.value]?.label ?? '') : '';
  const inputValue = props.multiple || open ? query : singleLabel;
  const singleLeading = !props.multiple && props.value ? lookup[props.value]?.leading : null;

  return (
    <Command
      shouldFilter={!loadOptions}
      loop
      label={props['aria-label']}
      className={cn('w-full', className)}
    >
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        <PopoverPrimitive.Anchor asChild>
          <div
            ref={anchorRef}
            className={fieldFrame(
              'auto',
              cn(
                size === 'md' ? 'min-h-10' : 'min-h-12',
                props.multiple && 'flex-wrap gap-1.5 py-1.5 pl-1.5',
              ),
            )}
          >
            {props.multiple ? (
              props.value.map((value) => {
                const option = lookup[value];
                return (
                  <span
                    key={value}
                    className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-full bg-pill-card pr-1 pl-0.5 text-label text-fg"
                  >
                    {option?.leading ? (
                      <span className="inline-flex shrink-0 [&>*]:size-6">{option.leading}</span>
                    ) : (
                      <span className="w-1.5" />
                    )}
                    <span className="truncate">{option?.label ?? value}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${option?.label ?? value}`}
                      disabled={disabled}
                      onClick={() => remove(value)}
                      className="inline-flex size-5 items-center justify-center rounded-full text-fg-secondary hover:bg-pill-card-hover hover:text-fg"
                    >
                      <XIcon size={12} aria-hidden="true" />
                    </button>
                  </span>
                );
              })
            ) : singleLeading ? (
              <span aria-hidden="true" className="inline-flex shrink-0 [&>*]:size-6">
                {singleLeading}
              </span>
            ) : null}
            {/* asChild: eigene Benennung (aria-label bzw. Field-Label) und aria-expanded gewinnen gegen
                die festen Werte von cmdk; die id bleibt die von cmdk, das braucht seine Fokusführung. */}
            <Command.Input asChild value={inputValue} onValueChange={changeQuery}>
              {/* aria-controls setzt cmdk auf die Liste (Slot-Merge), siehe Pickers.test. */}
              {/* eslint-disable jsx-a11y/role-has-required-aria-props */}
              <input
                role="combobox"
                onFocus={openList}
                onClick={openList}
                onBlur={onBlur}
                onKeyDown={onKeyDown}
                disabled={disabled}
                placeholder={props.multiple && props.value.length ? '' : placeholder}
                aria-label={props['aria-label']}
                aria-labelledby={props['aria-label'] ? undefined : field.labelId}
                aria-describedby={field['aria-describedby']}
                aria-invalid={field['aria-invalid']}
                aria-expanded={open}
                className="h-8 min-w-24 flex-1 bg-transparent text-body text-fg outline-none placeholder:text-fg-placeholder focus-visible:outline-none disabled:cursor-not-allowed"
              />
              {/* eslint-enable jsx-a11y/role-has-required-aria-props */}
            </Command.Input>
            <span aria-hidden="true" className={cn(fieldIcon, 'pr-2')}>
              <CaretDownIcon />
            </span>
          </div>
        </PopoverPrimitive.Anchor>
        <PopoverPrimitive.Portal>
          <PopoverPrimitive.Content
            align="start"
            sideOffset={8}
            collisionPadding={16}
            onOpenAutoFocus={(event) => event.preventDefault()}
            style={layerStyle}
            onInteractOutside={(event) => {
              const target = event.target as Node | null;
              if (target && anchorRef.current?.contains(target)) event.preventDefault();
            }}
            className={cn(
              floatingSurface,
              'w-(--radix-popover-trigger-width) min-w-64 overflow-hidden',
            )}
          >
            <Command.List className="max-h-80 overflow-y-auto p-1">
              {async.loading ? (
                <Command.Loading>
                  <OptionSkeleton />
                </Command.Loading>
              ) : (
                <>
                  <Command.Empty className="px-3 py-3 text-label text-fg-secondary">
                    {async.error ? 'Could not load suggestions' : emptyText}
                  </Command.Empty>
                  {visible.map((option) => (
                    <Command.Item
                      key={option.value}
                      value={option.value}
                      keywords={[option.label, option.description ?? '']}
                      disabled={option.disabled}
                      onSelect={() => select(option)}
                      className={cn(cmdkItem, 'min-h-12 pr-9')}
                    >
                      {option.leading ? (
                        <span className="inline-flex shrink-0 [&>*]:size-6">{option.leading}</span>
                      ) : null}
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{option.label}</span>
                        {option.description ? (
                          <span className="truncate text-label text-fg-secondary">
                            {option.description}
                          </span>
                        ) : null}
                      </span>
                      {isSelected(option.value) ? (
                        <CheckIcon
                          aria-hidden="true"
                          size={16}
                          weight="bold"
                          className="absolute right-3 text-accent-fg"
                        />
                      ) : null}
                    </Command.Item>
                  ))}
                </>
              )}
            </Command.List>
          </PopoverPrimitive.Content>
        </PopoverPrimitive.Portal>
      </PopoverPrimitive.Root>
    </Command>
  );
}
